import { useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, ArrowUpRight, Heart, Loader2, ShoppingBag, ShieldCheck } from 'lucide-react';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import CheckoutConsentModal from '@/components/CheckoutConsentModal';
import { EBIKES, CAMPING, HERITAGE_ESTATE, HERITAGE_BUNDLES } from '@/data/catalog';
import useWishlist from '@/hooks/useWishlist';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Flatten every catalogue entry into a single lookup so the dynamic
// /product/:slug route works for e-bikes, camping items, the whole
// Heritage Estate collection, and curated bundles without
// duplicating logic.
const ALL_PRODUCTS = [
  ...EBIKES.map((p) => ({ ...p, _collection: 'ebikes' })),
  ...CAMPING.map((p) => ({ ...p, _collection: 'camping' })),
  ...HERITAGE_ESTATE.map((p) => ({ ...p, _collection: 'heritage_estate' })),
  ...HERITAGE_BUNDLES.map((p) => ({ ...p, _collection: 'heritage_bundle' })),
];

export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const product = ALL_PRODUCTS.find((p) => p.slug === slug);
  const [busy, setBusy] = useState(false);
  const [buyError, setBuyError] = useState(null);
  const [consentOpen, setConsentOpen] = useState(false);
  const [activeGalleryIdx, setActiveGalleryIdx] = useState(0);
  const { isSaved, toggle } = useWishlist();

  if (!product) {
    return (
      <div className="App">
        <Navigation />
        <main data-testid="product-page-not-found" className="pt-44 min-h-[60vh]">
          <div className="max-w-3xl mx-auto px-6 lg:px-10 py-20 text-center">
            <span className="font-eyebrow">Not on the shelf</span>
            <h1 className="mt-4 font-display text-4xl text-[var(--brand-forest)]">
              We couldn&rsquo;t find that product.
            </h1>
            <p className="mt-4 text-[var(--brand-muted)]">
              It may have been removed during a Heritage Estate audit, or the link may have
              a typo. Head back to the collection and try again.
            </p>
            <div className="mt-8 flex items-center justify-center gap-3">
              <Link
                to="/heritage-estate"
                className="btn-gold px-5 py-2.5 text-sm font-semibold"
                data-testid="product-page-back-collection"
              >
                Heritage Estate collection
              </Link>
              <button
                onClick={() => navigate('/')}
                className="text-sm font-semibold text-[var(--brand-forest)] underline underline-offset-4"
              >
                Home
              </button>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const saved = isSaved(product.slug);
  const isHeritage = product._collection === 'heritage_estate';
  const isBundle = product._collection === 'heritage_bundle';

  const openConsent = () => {
    setBuyError(null);
    setConsentOpen(true);
  };

  const confirmAndCheckout = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/checkout/session`, {
        slug: product.slug,
        origin_url: window.location.origin,
      });
      if (res.data?.url) {
        window.location.href = res.data.url;
      } else {
        throw new Error('No checkout URL returned');
      }
    } catch (err) {
      setBuyError(err?.response?.data?.detail || 'Could not start checkout. Please try again.');
      setBusy(false);
      setConsentOpen(false);
    }
  };

  return (
    <div className="App">
      <Navigation />
      <main data-testid={`product-page-${product.slug}`} className="pt-44 bg-[var(--brand-parchment)]">
        {product.flagshipImage && (
          <section
            data-testid={`product-page-flagship-${product.slug}`}
            className="relative overflow-hidden bg-black"
          >
            <img
              src={product.flagshipImage.src}
              alt={product.flagshipImage.alt}
              className="w-full h-[42vh] sm:h-[52vh] lg:h-[62vh] object-cover"
              loading="eager"
            />
            {product.flagshipImage.caption && (
              <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/30 to-transparent pointer-events-none">
                <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 lg:py-8">
                  <div className="text-[10px] tracking-[0.24em] uppercase font-bold text-[var(--brand-gold)] mb-2">
                    Flagship · The Juliet
                  </div>
                  <p className="font-display text-lg sm:text-xl lg:text-2xl text-[var(--brand-parchment)] italic max-w-2xl leading-snug">
                    &ldquo;{product.flagshipImage.caption}&rdquo;
                  </p>
                </div>
              </div>
            )}
          </section>
        )}
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8">
          {/* Breadcrumb */}
          <nav className="text-xs uppercase tracking-[0.24em] text-[var(--brand-muted)] flex items-center gap-2">
            <Link to="/" className="hover:text-[var(--brand-forest)]">Home</Link>
            <span>·</span>
            {isHeritage || isBundle ? (
              <Link to="/heritage-estate" className="hover:text-[var(--brand-forest)]">Heritage Estate</Link>
            ) : (
              <Link to={`/#${product._collection}`} className="hover:text-[var(--brand-forest)]">
                {product._collection === 'ebikes' ? 'E-Bikes' : 'Camping'}
              </Link>
            )}
            <span>·</span>
            <span className="text-[var(--brand-forest)]">{product.name}</span>
          </nav>

          <div className="mt-8 grid lg:grid-cols-2 gap-10 lg:gap-16 items-start">
            {/* Image column */}
            <div className="relative">
              <div className="relative aspect-square overflow-hidden bg-[var(--brand-parchment-warm)] border border-[var(--brand-stone)]">
                <img
                  src={
                    Array.isArray(product.gallery) && product.gallery.length > 0
                      ? product.gallery[activeGalleryIdx]?.src || product.image
                      : product.image
                  }
                  alt={
                    Array.isArray(product.gallery) && product.gallery.length > 0
                      ? product.gallery[activeGalleryIdx]?.alt || product.name
                      : product.name
                  }
                  data-testid={`product-page-image-${product.slug}`}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                {product.sku && (
                  <span className="absolute bottom-4 left-4 bg-[var(--brand-parchment)]/95 text-[10px] tracking-[0.24em] uppercase px-2.5 py-1 font-semibold text-[var(--brand-forest)]">
                    {product.sku}
                  </span>
                )}
                {product.badge && (
                  <span className="absolute top-4 left-4 bg-[var(--brand-forest)] text-[var(--brand-parchment)] text-[10px] tracking-[0.24em] uppercase px-2.5 py-1">
                    {product.badge}
                  </span>
                )}
              </div>
              {Array.isArray(product.gallery) && product.gallery.length > 1 && (
                <div
                  data-testid={`product-page-gallery-${product.slug}`}
                  className="mt-3 grid grid-cols-5 gap-2"
                  role="tablist"
                  aria-label={`${product.name} — angles`}
                >
                  {product.gallery.map((g, i) => (
                    <button
                      key={g.src}
                      role="tab"
                      aria-selected={i === activeGalleryIdx}
                      data-testid={`product-page-gallery-thumb-${product.slug}-${i}`}
                      onClick={() => setActiveGalleryIdx(i)}
                      title={g.label}
                      className={
                        'relative aspect-square overflow-hidden border transition-all ' +
                        (i === activeGalleryIdx
                          ? 'border-[var(--brand-gold)] ring-1 ring-[var(--brand-gold)]'
                          : 'border-[var(--brand-stone)] hover:border-[var(--brand-forest)] opacity-80 hover:opacity-100')
                      }
                    >
                      <img
                        src={g.src}
                        alt={g.alt}
                        className="absolute inset-0 w-full h-full object-cover"
                        loading="lazy"
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Content column */}
            <div>
              <span className="font-eyebrow">
                {isBundle
                  ? `Heritage Estate · Curated Bundle`
                  : `${isHeritage ? 'Heritage Estate' : product._collection === 'ebikes' ? 'E-Bikes' : 'Field Kit'}${product.category ? ` · ${product.category}` : ''}`}
              </span>
              <h1
                data-testid={`product-page-title-${product.slug}`}
                className="mt-3 font-display text-4xl sm:text-5xl text-[var(--brand-forest)] leading-[1.05] tracking-tight"
              >
                {product.name}
              </h1>
              {product.subtitle && (
                <p className="mt-2 italic text-[var(--brand-muted)] text-lg">
                  {product.subtitle}
                </p>
              )}
              <div className="mt-6 flex items-baseline gap-4 flex-wrap">
                <span
                  data-testid={`product-page-price-${product.slug}`}
                  className="font-display text-3xl text-[var(--brand-olive)] font-semibold"
                >
                  {product.price}
                </span>
                {isBundle && product.rrp && (
                  <>
                    <span className="text-base text-[var(--brand-muted)] line-through">
                      {product.rrp}
                    </span>
                    <span
                      data-testid={`product-page-saving-${product.slug}`}
                      className="text-xs uppercase tracking-[0.22em] font-bold bg-[var(--brand-gold)]/20 text-[var(--brand-forest-deep)] px-2.5 py-1.5"
                    >
                      {product.saving}
                    </span>
                  </>
                )}
              </div>

              <p className="mt-6 text-[var(--brand-ink)] text-base leading-relaxed">
                {product.blurb}
              </p>

              {isBundle && Array.isArray(product.includes) && (
                <div
                  data-testid={`product-page-bundle-includes-${product.slug}`}
                  className="mt-6 bg-[var(--brand-parchment-warm)] border border-[var(--brand-stone)] p-4"
                >
                  <div className="font-eyebrow mb-3">What&rsquo;s in the kit</div>
                  <ul className="space-y-2">
                    {product.includes.map((item) => (
                      <li
                        key={item.slug}
                        className="flex items-baseline justify-between gap-3 text-sm"
                      >
                        <Link
                          to={`/product/${item.slug}`}
                          className="text-[var(--brand-forest)] hover:text-[var(--brand-olive)] font-semibold underline underline-offset-2"
                        >
                          {item.name}
                        </Link>
                        <span className="text-[var(--brand-muted)] whitespace-nowrap">
                          {item.price}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {product.summary && product.summary !== product.blurb && (
                <p
                  data-testid={`product-page-summary-${product.slug}`}
                  className="mt-4 pl-4 border-l-2 border-[var(--brand-gold)] text-sm italic text-[var(--brand-muted)] leading-relaxed"
                >
                  {product.summary}
                </p>
              )}

              {Array.isArray(product.spec) && product.spec.length > 0 && (
                <div className="mt-8">
                  <div className="font-eyebrow mb-3">Specification</div>
                  <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm text-[var(--brand-ink)]">
                    {product.spec.map((s) => (
                      <li key={s} className="flex items-center gap-2">
                        <span className="w-1.5 h-1.5 bg-[var(--brand-gold)]" />
                        {s}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* CTA row */}
              <div className="mt-8 grid grid-cols-2 gap-3 max-w-md">
                <button
                  data-testid={`product-page-save-${product.slug}`}
                  onClick={() => toggle({ slug: product.slug, name: product.name, price: product.price, image: product.image })}
                  className={
                    'min-h-[52px] border text-sm font-semibold inline-flex items-center justify-center gap-2 transition-colors ' +
                    (saved
                      ? 'border-[var(--brand-gold)] text-[var(--brand-gold)] bg-[var(--brand-gold)]/10'
                      : 'border-[var(--brand-forest)] text-[var(--brand-forest)] hover:bg-[var(--brand-forest)] hover:text-[var(--brand-parchment)]')
                  }
                >
                  <Heart size={15} className={saved ? 'fill-[var(--brand-gold)]' : ''} />
                  {saved ? 'Saved' : 'Save for later'}
                </button>
                <button
                  data-testid={`product-page-buy-${product.slug}`}
                  onClick={openConsent}
                  disabled={busy}
                  className="btn-gold min-h-[52px] text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-wait"
                >
                  {busy ? (
                    <>
                      <Loader2 size={14} className="animate-spin" /> Starting…
                    </>
                  ) : (
                    <>
                      <ShoppingBag size={14} /> Buy now
                    </>
                  )}
                </button>
              </div>

              {buyError && (
                <p
                  data-testid={`product-page-buy-error-${product.slug}`}
                  className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 px-4 py-3"
                >
                  {buyError}
                </p>
              )}

              {product.disclaimer && (
                <div
                  data-testid={`product-page-disclaimer-${product.slug}`}
                  className="mt-8 bg-[var(--brand-parchment-warm)] border border-[var(--brand-olive)]/25 border-l-4 border-l-[var(--brand-gold)] px-4 py-3"
                >
                  <div className="flex items-start gap-2">
                    <ShieldCheck size={14} className="shrink-0 mt-0.5 text-[var(--brand-forest-deep)]" strokeWidth={2.2} />
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[var(--brand-forest-deep)]">
                        {product.disclaimer.label}
                      </div>
                      <p className="mt-1 text-xs leading-relaxed text-[var(--brand-ink)]">
                        {product.disclaimer.text}{' '}
                        <Link to="/disclaimer" className="link-expand font-semibold text-[var(--brand-forest-deep)]">
                          Full disclaimer &rarr;
                        </Link>
                      </p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mt-10">
                {isHeritage || isBundle ? (
                  <Link
                    to="/heritage-estate"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--brand-forest)] link-expand"
                    data-testid={`product-page-back-${product.slug}`}
                  >
                    <ArrowLeft size={14} /> Back to Heritage Estate collection
                  </Link>
                ) : (
                  <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--brand-forest)] link-expand"
                    data-testid={`product-page-back-${product.slug}`}
                  >
                    <ArrowLeft size={14} /> Back to shop
                  </Link>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Complete Kit — bike + companion camping gear */}
        {product.completeKit && (
          <section
            data-testid={`product-page-complete-kit-${product.slug}`}
            className="mt-4 bg-[var(--brand-forest-deep)] text-[var(--brand-parchment)] overflow-hidden"
          >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20 grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-center">
              <div className="relative aspect-[4/3] overflow-hidden order-2 lg:order-1">
                <img
                  src={product.completeKit.heroImage}
                  alt={product.completeKit.heroAlt}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </div>
              <div className="order-1 lg:order-2">
                <span className="font-eyebrow !text-[var(--brand-gold)]">
                  Beyond the bike
                </span>
                <h2 className="mt-3 font-display text-3xl sm:text-4xl leading-[1.05] tracking-tight">
                  {product.completeKit.headline}
                </h2>
                <p className="mt-5 text-base opacity-90 leading-relaxed max-w-xl">
                  {product.completeKit.lead}
                </p>
                <div
                  data-testid={`product-page-complete-kit-companions-${product.slug}`}
                  className="mt-8 space-y-3"
                >
                  {product.completeKit.companions.map((cslug) => {
                    const c = ALL_PRODUCTS.find((x) => x.slug === cslug);
                    if (!c) return null;
                    return (
                      <Link
                        to={`/product/${c.slug}`}
                        key={c.slug}
                        data-testid={`product-page-companion-${c.slug}`}
                        className="group flex items-center gap-4 bg-[var(--brand-parchment)]/8 hover:bg-[var(--brand-parchment)]/12 border border-[var(--brand-parchment)]/12 hover:border-[var(--brand-gold)]/50 px-4 py-3 transition-colors"
                      >
                        <div className="w-14 h-14 shrink-0 bg-[var(--brand-parchment)]/10 overflow-hidden">
                          <img
                            src={c.image}
                            alt=""
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-display text-lg text-[var(--brand-parchment)] truncate">
                            {c.name}
                          </div>
                          {(c.summary || c.subtitle) && (
                            <div className="text-xs text-[var(--brand-parchment)]/70 truncate">
                              {c.subtitle || c.summary}
                            </div>
                          )}
                        </div>
                        <div className="text-right shrink-0">
                          <div className="font-display text-base font-semibold text-[var(--brand-gold)]">
                            {c.price}
                          </div>
                          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--brand-parchment)]/60 group-hover:text-[var(--brand-gold)]">
                            View &rarr;
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </div>
          </section>
        )}

        <CheckoutConsentModal
          product={consentOpen ? product : null}
          busy={busy}
          onCancel={() => setConsentOpen(false)}
          onConfirm={confirmAndCheckout}
        />
      </main>
      <Footer />
    </div>
  );
}
